import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Checkbox,
  IconButton as ChakraIconButton,
  HStack,
  useColorModeValue,
  useBoolean,
  Portal,
  VStack,
  Button,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tag, TagLabel
} from '@chakra-ui/react';
import {
  FiEdit2,
  FiTrash2,
  FiCalendar,
  FiFlag,
  FiClock,
  FiCheck,
  FiX,
  FiMoreVertical,
  FiAlertCircle,
  FiFolder,
  FiPlay
} from 'react-icons/fi';
import { Task } from '../../../types/task';
import { Project } from '../../../types/project';
import { getProjectColor } from '../../../utils/colorUtils';
import { useTranslation } from 'react-i18next';
import { Tag as TagType } from '../../../types/tag';

export interface TaskItemRenderOptions {
  showProjectName?: boolean;
  showDetailedTime?: boolean;
  projectsMap?: Record<string, Project>;
}

// Modified date formatting functions to accept translation function
export const formatDateSimple = (dateString?: string, t?: any): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);

  // Use passed t function or fallback to direct string
  const translate = (key: string, fallback: string) => t ? t(key) : fallback;

  if (date.getTime() === today.getTime()) return translate('timeGroups.today', '今天');
  if (date.getTime() === tomorrow.getTime()) return translate('timeGroups.tomorrow', '明天');
  if (date.getTime() === yesterday.getTime()) return translate('timeGroups.yesterday', '昨天');
  if (date < today) return translate('timeStatus.overdue', '已过期');

  return new Intl.DateTimeFormat('zh-CN', { month: 'short', day: 'numeric' }).format(date);
};

// 详细格式化日期函数 - 包含时间
export const formatDateDetailed = (dateString?: string, t?: any): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);

  // Use passed t function or fallback to direct string
  const translate = (key: string, fallback: string) => t ? t(key) : fallback;

  // 日期前缀
  let prefix = '';
  if (date.getTime() === today.getTime()) prefix = translate('timeGroups.today', '今天') + ' ';
  else if (date.getTime() === tomorrow.getTime()) prefix = translate('timeGroups.tomorrow', '明天') + ' ';
  else if (date.getTime() === yesterday.getTime()) prefix = translate('timeGroups.yesterday', '昨天') + ' ';
  else if (date < today) prefix = translate('timeStatus.overdue', '已过期') + ' ';
  else prefix = new Intl.DateTimeFormat('zh-CN', { month: 'short', day: 'numeric' }).format(date) + ' ';

  // 时间部分
  const timeFormat = new Intl.DateTimeFormat('zh-CN', { hour: '2-digit', minute: '2-digit' }).format(date);
  return prefix + timeFormat;
};

// --- TaskItemBase Component ---
export const TaskItemBase = ({
  task,
  onSelect,
  onToggleDone,
  onDelete,
  options = {}
}: {
  task: Task & { tags?: TagType[] };
  onSelect: (task: Task) => void;
  onToggleDone: (task: Task, done: boolean) => void;
  onDelete: (id: string) => void;
  options?: TaskItemRenderOptions;
}) => {
  const {
    showProjectName = false,
    showDetailedTime = false,
    projectsMap = {}
  } = options;

  const { t } = useTranslation(['v0/tasks', 'v0/common']);

  // 这些状态和ref都是来自原始TaskItem的
  const textColor = useColorModeValue('gray.800', 'gray.200');
  const textDoneColor = useColorModeValue('gray.500', 'gray.500');
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const pointerStartRef = useRef<{ x: number; y: number; time: number; pointerId: number } | null>(null);
  const [isDragging, setIsDragging] = useBoolean(false);
  const [dragDistance, setDragDistance] = useState(0);
  const [swipeDir, setSwipeDir] = useState<'none' | 'left' | 'right'>('none');
  const actionThreshold = 80;
  const dragThreshold = 5;
  const clickTimeout = 250;
  const bgColorNormal = useColorModeValue('white', 'gray.800');
  const bgColorDone = useColorModeValue('gray.50', 'gray.700');
  const bgColorRightAction = useColorModeValue('green.50', 'green.900');
  const bgColorLeftAction = useColorModeValue('red.50', 'red.900');
  const bgColorRightActionDone = useColorModeValue('green.100', 'green.800');
  const bgColorLeftActionDone = useColorModeValue('red.100', 'red.800');
  const didPointerDownOnControl = useRef(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // 项目名称（如果需要显示） - 使用 useMemo 优化
  const projectName = useMemo(() =>
    showProjectName && task.projectId ? (projectsMap[task.projectId]?.title || '') : ''
  , [showProjectName, task.projectId, projectsMap]);

  // 项目色彩（如果有项目ID） - 使用 useMemo 优化
  const projectColor = useMemo(() =>
    task.projectId ? getProjectColor(task.projectId) : ''
  , [task.projectId]);

  // 格式化截止日期，根据选项决定显示简单版还是详细版 - 使用 useCallback 优化
  const getFormattedDate = useCallback((date?: string) => {
    return showDetailedTime
      ? formatDateDetailed(date, t)
      : formatDateSimple(date, t);
  }, [showDetailedTime, t]);

  // 判断是否已过期 - 使用 useMemo 优化
  const isOverdue = useMemo(() =>
    task.dueAt ? new Date(task.dueAt) < new Date() && !task.done : false
  , [task.dueAt, task.done]);

  // 以下是与原TaskItem相同的交互处理逻辑 - 使用 useCallback 优化

  const resetInteractionState = useCallback(() => {
      pointerStartRef.current = null;
      setIsDragging.off();
      setDragDistance(0);
      setSwipeDir('none');
      didPointerDownOnControl.current = false;
      if (itemRef.current) {
          itemRef.current.style.transition = 'transform 0.2s, background 0.2s';
          itemRef.current.style.transform = 'translateX(0)';
          itemRef.current.style.background = task.done ? bgColorDone : bgColorNormal;
          itemRef.current.style.userSelect = '';
      }
  }, [setIsDragging, task.done, bgColorDone, bgColorNormal]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    didPointerDownOnControl.current = false;
    if (isDeleting || e.button !== 0 || !e.isPrimary || pointerStartRef.current) return;

    const isDownOnMenu = menuButtonRef.current && menuButtonRef.current.contains(e.target as Node);
    const checkbox = itemRef.current?.querySelector('input[type="checkbox"]');
    const isDownOnCheckbox = checkbox && checkbox.contains(e.target as Node);

    if (isDownOnMenu || isDownOnCheckbox) {
        didPointerDownOnControl.current = true;
        return;
    }

    try {
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      pointerStartRef.current = { x: e.clientX, y: e.clientY, time: Date.now(), pointerId: e.pointerId };
      setIsDragging.off();
      setDragDistance(0);
      setSwipeDir('none');
      if (itemRef.current) itemRef.current.style.transition = 'none';
    } catch (err) { console.warn("PointerDown capture failed:", err); }
  }, [isDeleting, setIsDragging]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointerStartRef.current || e.pointerId !== pointerStartRef.current.pointerId || isDeleting) return;
    const distance = e.clientX - pointerStartRef.current.x;
    if (!isDragging) {
      if (Math.abs(distance) > dragThreshold) {
        setIsDragging.on(); e.preventDefault();
        if (itemRef.current) itemRef.current.style.userSelect = 'none';
      } else { return; }
    }
    e.preventDefault(); setDragDistance(distance);
    const currentSwipeDir = distance > dragThreshold ? 'right' : (distance < -dragThreshold ? 'left' : 'none');
    setSwipeDir(currentSwipeDir);
    if (itemRef.current) {
      const limitedDistance = Math.max(Math.min(distance, 100), -100);
      itemRef.current.style.transform = `translateX(${limitedDistance}px)`;
      if (currentSwipeDir === 'right') itemRef.current.style.background = limitedDistance > actionThreshold ? bgColorRightAction : (task.done ? bgColorDone : bgColorNormal);
      else if (currentSwipeDir === 'left') itemRef.current.style.background = limitedDistance < -actionThreshold ? bgColorLeftAction : (task.done ? bgColorDone : bgColorNormal);
      else itemRef.current.style.background = task.done ? bgColorDone : bgColorNormal;
    }
  }, [pointerStartRef, isDeleting, isDragging, setIsDragging, setDragDistance, setSwipeDir, actionThreshold, dragThreshold, task.done, bgColorRightAction, bgColorLeftAction, bgColorDone, bgColorNormal]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointerStartRef.current || e.pointerId !== pointerStartRef.current.pointerId || isDeleting) return;
    const { pointerId, startTime, startX } = { pointerId: pointerStartRef.current.pointerId, startTime: pointerStartRef.current.time, startX: pointerStartRef.current.x };
    const wasDragging = isDragging;
    try { (e.target as HTMLElement).releasePointerCapture(pointerId); } catch (err) { console.warn("PointerUp release failed:", err); }
    const pressDuration = Date.now() - startTime;
    const finalDistance = e.clientX - startX;
    const distanceMoved = Math.abs(finalDistance);
    if (itemRef.current) { itemRef.current.style.transition = 'transform 0.2s, background 0.2s'; itemRef.current.style.userSelect = ''; }

    if (wasDragging) {
      const currentSwipeDir = finalDistance > dragThreshold ? 'right' : (finalDistance < -dragThreshold ? 'left' : 'none');
      if (currentSwipeDir === 'right' && finalDistance > actionThreshold) {
        onToggleDone(task, !task.done);
        if (itemRef.current) itemRef.current.style.background = bgColorRightActionDone;
        setTimeout(resetInteractionState, 50);
      } else if (currentSwipeDir === 'left' && finalDistance < -actionThreshold) {
        setShowDeleteConfirm(true);
        resetInteractionState();
      } else { resetInteractionState(); }
    } else if (distanceMoved < dragThreshold && pressDuration < clickTimeout) {
      if (didPointerDownOnControl.current) {
           resetInteractionState();
           return;
      }

      const isClickOnMenuButton = menuButtonRef.current && menuButtonRef.current.contains(e.target as Node);
      const checkbox = itemRef.current?.querySelector('input[type="checkbox"]');
      const isClickOnCheckbox = checkbox && checkbox.contains(e.target as Node);

      if (!isClickOnMenuButton && !isClickOnCheckbox) {
           onSelect(task);
      }
      resetInteractionState();
    } else {
       resetInteractionState();
    }
  }, [pointerStartRef, isDeleting, isDragging, dragThreshold, actionThreshold, clickTimeout, resetInteractionState, onToggleDone, task, bgColorRightActionDone, onSelect]);

  const handleMenuClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  }, [showMenu]);

  const handleDeleteFromMenu = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setShowMenu(false);
      setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    setIsDeleting(true);
    if (itemRef.current) {
      itemRef.current.style.background = bgColorLeftActionDone;
      itemRef.current.style.transform = 'translateX(-100%)';
    }
    setTimeout(() => { onDelete(task.id); }, 300);
  }, [bgColorLeftActionDone, onDelete, task.id]);

  const renderSwipeHints = (): React.ReactNode => {
    if (!isDragging) return null;
    return (
      <>
        {swipeDir === 'left' && dragDistance < -actionThreshold && (
          <Box position="absolute" right="10px" top="50%" transform="translateY(-50%)" color="red.500" opacity={Math.min(Math.abs(dragDistance / actionThreshold), 1)} zIndex={5}>
            <FiTrash2 size={20} />
          </Box>
        )}
        {swipeDir === 'right' && dragDistance > actionThreshold && (
          <Box position="absolute" left="10px" top="50%" transform="translateY(-50%)" color="green.500" opacity={Math.min(dragDistance / actionThreshold, 1)} zIndex={5}>
            {task.done ? <FiX size={20} /> : <FiCheck size={20} />}
          </Box>
        )}
      </>
    );
  };

  const renderSwipeBackground = (): React.ReactNode => {
    if (!isDragging || swipeDir === 'none' || Math.abs(dragDistance) < 5) return null;
    return (
      <>
        {swipeDir === 'left' && (
          <Box position="absolute" right="0" top="0" bottom="0" width="60px" bg="red.500" opacity={Math.min(Math.abs(dragDistance / actionThreshold) * 0.8, 0.8)} zIndex={1} display="flex" alignItems="center" justifyContent="center">
            <FiTrash2 color="white" size={24} />
          </Box>
        )}
        {swipeDir === 'right' && (
          <Box position="absolute" left="0" top="0" bottom="0" width="60px" bg="green.500" opacity={Math.min(Math.abs(dragDistance / actionThreshold) * 0.8, 0.8)} zIndex={1} display="flex" alignItems="center" justifyContent="center">
            {task.done ? <FiX color="white" size={24} /> : <FiCheck color="white" size={24} />}
          </Box>
        )}
      </>
    );
  };

  useEffect(() => {
      if (isDeleting && !task) {
          setIsDeleting(false);
          resetInteractionState();
      }
  }, [task, isDeleting]);

  // 判断是否已过期已在上面使用 useMemo 优化

  return (
    <>
      <Box
        position="relative"
        overflow="hidden"
        opacity={isDeleting ? 0 : 1}
        height={isDeleting ? '0' : 'auto'}
        transition="opacity 0.3s, height 0.3s"
        borderBottomWidth={{base: "0", md: "1px"}}
        borderColor={useColorModeValue("gray.100", "gray.700")}
      >
        {renderSwipeBackground()}
        <Box
          py={{base: 2, md: 2.5}}
          px={3}
          bg={task.done ? bgColorDone : bgColorNormal}
          _hover={!isDragging ? { bg: task.done ? useColorModeValue('gray.100', 'gray.600') : useColorModeValue('gray.50', 'gray.700') } : {}}
          position="relative"
          cursor={isDeleting ? 'default' : (isDragging ? 'grabbing' : 'pointer')}
          ref={itemRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{ transform: 'translateX(0)' }}
          sx={{ touchAction: 'pan-y' }}
          zIndex={2}
        >
          {renderSwipeHints()}
          <Flex align="center" justify="space-between">
            <Flex align="center" flex="1" minW="0" mr={2}>
              <Checkbox
                isChecked={task.done}
                onChange={(e) => onToggleDone(task, e.target.checked)}
                colorScheme="pink"
                size="lg"
                mr={3}
                aria-label={t('v0/tasks:actions.toggleTaskStatus', { title: task.title, status: task.done ? t('v0/tasks:status.incomplete') : t('v0/tasks:status.complete') })}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              />
              <Flex direction="column" minW="0">
                <Flex align="center">
                  <Text
                    fontWeight="medium"
                    fontSize="md"
                    noOfLines={1}
                    textDecoration={task.done ? 'line-through' : 'none'}
                    color={task.done ? textDoneColor : textColor}
                    mr={task.tags && task.tags.length > 0 ? 2 : 0}
                  >
                    {task.title}
                    {isOverdue && !task.done && (
                      <Box as="span" ml={1} color="red.500">
                        <FiAlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle' }} />
                      </Box>
                    )}
                  </Text>
                  {task.tags && task.tags.length > 0 && (
                    <HStack spacing={1} display={{ base: 'none', md: 'flex' }} >
                      {task.tags.slice(0, 3).map((tag) => (
                        <Tag size="sm" key={tag.id} variant="subtle" colorScheme="gray" borderRadius="full">
                          <TagLabel>{tag.title}</TagLabel>
                        </Tag>
                      ))}
                      {task.tags.length > 3 && (
                        <Tag size="sm" variant="subtle" colorScheme="gray" borderRadius="full">
                          <TagLabel>...</TagLabel>
                        </Tag>
                      )}
                    </HStack>
                  )}
                </Flex>

                <Flex align="center" mt={0.5} flexWrap="wrap">
                  {/* 开始时间显示 */}
                  {task.startAt && (
                    <HStack spacing={1} mr={2}>
                      <Box as={FiPlay} size="12px" color="green.500" />
                      <Tooltip
                        label={showDetailedTime ? undefined : formatDateDetailed(task.startAt, t)}
                        hasArrow
                        placement="top"
                        isDisabled={!task.startAt || showDetailedTime}
                      >
                        <Text
                          fontSize="xs"
                          color="green.500"
                          textDecoration={task.done ? 'line-through' : 'none'}
                        >
                          {getFormattedDate(task.startAt)}
                        </Text>
                      </Tooltip>
                    </HStack>
                  )}

                  {/* 预计耗时显示 */}
                  {task.cost && (
                    <HStack spacing={1} mr={2}>
                      <Box as={FiClock} size="12px" color="blue.500" />
                      <Tooltip
                        label={t('v0/tasks:form.dates.duration')}
                        hasArrow
                        placement="top"
                      >
                        <Text
                          fontSize="xs"
                          color="blue.500"
                          textDecoration={task.done ? 'line-through' : 'none'}
                        >
                          {task.cost >= 86400
                            ? t('v0/common:durationSettings.days', { count: Math.floor(task.cost / 86400) })
                            : task.cost >= 3600
                              ? t('v0/common:durationSettings.hours', { count: Math.floor(task.cost / 3600) })
                              : t('v0/common:durationSettings.minutes', { count: Math.floor(task.cost / 60) })}
                        </Text>
                      </Tooltip>
                    </HStack>
                  )}

                  {/* 截止日期显示 */}
                  {task.dueAt && (
                    <HStack spacing={1} mr={2}>
                      <Box as={FiFlag} size="12px" color={isOverdue && !task.done ? "red.500" : "gray.500"} />
                      <Tooltip
                        label={showDetailedTime ? undefined : formatDateDetailed(task.dueAt, t)}
                        hasArrow
                        placement="top"
                        isDisabled={!task.dueAt || showDetailedTime}
                      >
                        <Text
                          fontSize="xs"
                          color={isOverdue && !task.done ? "red.500" : "gray.500"}
                          textDecoration={task.done ? 'line-through' : 'none'}
                        >
                          {getFormattedDate(task.dueAt)}
                        </Text>
                      </Tooltip>
                    </HStack>
                  )}

                  {/* 项目名称显示 */}
                  {showProjectName && projectName && (
                    <HStack spacing={1} mr={2}>
                      <Box as={FiFolder} size="12px" color={projectColor} />
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        color={projectColor}
                        textDecoration={task.done ? 'line-through' : 'none'}
                      >
                        {projectName}
                      </Text>
                    </HStack>
                  )}

                  {/* Mobile Tags Display (optional, if needed) */}
                  {task.tags && task.tags.length > 0 && (
                    <HStack spacing={1} display={{ base: 'flex', md: 'none' }} mt={1} >
                      {task.tags.slice(0, 3).map((tag) => (
                        <Tag size="sm" key={tag.id} variant="subtle" colorScheme="gray" borderRadius="full">
                          <TagLabel>{tag.title}</TagLabel>
                        </Tag>
                      ))}
                      {task.tags.length > 3 && (
                        <Tag size="sm" variant="subtle" colorScheme="gray" borderRadius="full">
                          <TagLabel>...</TagLabel>
                        </Tag>
                      )}
                    </HStack>
                  )}

                  {/* 任务备注显示 */}
                  {task.notes && (
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      noOfLines={1}
                      flex="1"
                      textDecoration={task.done ? 'line-through' : 'none'}
                      opacity={task.done ? 0.7 : 0.9}
                    >
                      {task.notes}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Flex>

            <Flex>
              <HStack spacing={1}>
                {task.vital && <Box as={FiFlag} size="14px" color="red.500" aria-label={t('v0/tasks:form.priority.vital')} />}
                {task.urgent && <Box as={FiClock} size="14px" color="orange.500" aria-label={t('v0/tasks:form.priority.urgent')} />}
                <ChakraIconButton
                  ref={menuButtonRef}
                  aria-label={t('v0/tasks:actions.taskOptions')}
                  icon={<FiMoreVertical />}
                  size="sm"
                  variant="ghost"
                  color="gray.400"
                  onClick={handleMenuClick}
                  onPointerDown={(e) => e.stopPropagation()}
                />
              </HStack>

              {showMenu && (
                <Portal>
                  <Box
                    position="fixed"
                    right={menuButtonRef.current ? `${window.innerWidth - menuButtonRef.current.getBoundingClientRect().right + 5}px` : "10px"}
                    top={menuButtonRef.current ? `${menuButtonRef.current.getBoundingClientRect().bottom}px` : "auto"}
                    zIndex="popover"
                    bg={useColorModeValue("white", "gray.700")}
                    boxShadow="lg"
                    borderRadius="md"
                    p={1}
                    onClick={(e) => e.stopPropagation()}
                    onMouseLeave={() => setShowMenu(false)}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <VStack spacing={0} align="stretch">
                      <Button
                        size="sm"
                        variant="ghost"
                        leftIcon={<FiEdit2 />}
                        justifyContent="flex-start"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(task);
                          setShowMenu(false);
                        }}
                      >
                        {t('v0/common:actions.edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        leftIcon={<FiTrash2 />}
                        justifyContent="flex-start"
                        colorScheme="red"
                        onClick={handleDeleteFromMenu}
                      >
                        {t('v0/common:actions.delete')}
                      </Button>
                    </VStack>
                  </Box>
                </Portal>
              )}
            </Flex>
          </Flex>
        </Box>
      </Box>

      <AlertDialog
        isOpen={showDeleteConfirm}
        leastDestructiveRef={cancelRef}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('v0/tasks:dialogs.deleteTask.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('v0/tasks:dialogs.deleteTask.confirmation', { title: task.title })}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setShowDeleteConfirm(false)}>
                {t('v0/common:actions.cancel')}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                {t('v0/common:actions.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};